import { SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import Layout from "../../app/layout/Layout";
import { Site } from "../../app/models/site";
import agent from "../../app/api/agent";
import LoadingComponent from "../../app/layout/LoadingComponent";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useOrganization } from "@clerk/clerk-react";
import {
  EuiEmptyPrompt,
  EuiLink,
  EuiSelect,
  EuiSpacer,
  EuiSuperDatePicker,
  EuiSuperDatePickerProps,
  EuiTitle,
  OnRefreshProps,
  OnTimeChangeProps,
} from "@elastic/eui";
import { EuiPageHeader, EuiButton } from "@elastic/eui";
import { Add } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useFlow } from "@frigade/react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionActions from "@mui/material/AccordionActions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import Typography from "@mui/material/Typography";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

export default function Compliance() {
  const [selectedOption, setSelectedOption] = useState("");
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState<Site[]>([]);
  const { organization } = useOrganization();

  const handleOptionChange = (event: any) => {
    setSelectedOption(event.target.value);
  };

  const [recentlyUsedRanges, setRecentlyUsedRanges] = useState<
    NonNullable<EuiSuperDatePickerProps["recentlyUsedRanges"]>
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const [start, setStart] = useState("now-12h");
  const [end, setEnd] = useState("now");

  const [refreshKey, setRefreshKey] = useState(Date.now());

  const onTimeChange = ({ start, end }: OnTimeChangeProps) => {
    const recentlyUsedRange = recentlyUsedRanges.filter((recentlyUsedRange) => {
      const isDuplicate =
        recentlyUsedRange.start === start && recentlyUsedRange.end === end;
      return !isDuplicate;
    });
    recentlyUsedRange.unshift({ start, end });
    setStart(start);
    setEnd(end);
    setRecentlyUsedRanges(
      recentlyUsedRange.length > 10
        ? recentlyUsedRange.slice(0, 9)
        : recentlyUsedRange
    );
    setIsLoading(true);
    startLoading();
  };

  const onRefresh = ({ start, end }: OnRefreshProps) => {
    setRefreshKey(Date.now()); // force a re-render
    setIsLoading(true);
    startLoading();
  };

  const startLoading = () => {
    setTimeout(stopLoading, 1000);
  };

  const stopLoading = () => {
    setIsLoading(false);
  };

  const { flow } = useFlow("flow_6fi97SYs");

  useEffect(() => {
    setLoading(true);
    agent.Catalog.list()
      .then((sites) => {
        setSites(sites);
        if (sites.length > 0) {
          flow?.steps.get("step-5")?.complete();
        }
      })
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  }, [organization?.id, flow]);

  const siteArray = sites.map((site) => site.hackgatedName);

  if (loading) return <LoadingComponent message="Loading website data..." />;

  if (sites.length === 0)
    return (
      <Layout>
        <EuiPageHeader
          pageTitle="Pentest Compliance Module"
          // iconType="logoKibana"
          description="Analyzes pentest traffic against a standardized framework to ensure reliable, high-quality testing and support regulatory compliance."
          // rightSideItems={[
          //   <EuiButton fill>Add something</EuiButton>,
          //   <EuiButton>Do something</EuiButton>,
          // ]}
          paddingSize="none"
        />
        <EuiSpacer />

        <Card variant="outlined" sx={{ mb: 2, borderRadius: "12px" }}>
          <CardContent>
            <Box
              sx={{
                height: 400,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EuiEmptyPrompt
                actions={[
                  <EuiButton color="primary" fill>
                    Add a website
                  </EuiButton>,
                ]}
                body={<p>Add a new case or change your filter settings.</p>}
                color="subdued"
                // iconType="logoSecurity"
                layout="vertical"
                title={<h2>Start adding websites</h2>}
                titleSize="m"
              />
            </Box>
          </CardContent>
        </Card>
      </Layout>
    );

  return (
    <Layout>
      <EuiPageHeader
        pageTitle="Pentest Compliance Module"
        // iconType="logoKibana"
        description="Analyzes pentest traffic against a standardized framework to ensure reliable, high-quality testing and support regulatory compliance."
        // rightSideItems={[
        //   <EuiButton fill>Add something</EuiButton>,
        //   <EuiButton>Do something</EuiButton>,
        // ]}
        paddingSize="none"
      />
      <EuiSpacer />

      {/* <EuiSelect
        options={siteArray.map((hackgatedName, index) => ({ value: hackgatedName, text: hackgatedName }))}
        onChange={(e) => {
          // Handle the select change event
          console.log('Selected value:', e.target.value);
        }}
      /> */}

      <Grid container alignItems="center" spacing={3} mb={3}>
        <Grid item xs={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="site-select-label">Select a site</InputLabel>
            <Select
              labelId="site-select-label"
              id="site-select"
              value={selectedOption}
              label="Select a site"
              onChange={handleOptionChange}
              sx={{ borderRadius: "8px" }}
            >
              {sites.map((sites) => (
                <MenuItem key={sites.hackgatedName} value={sites.hackgatedName}>
                  {sites.hackgatedName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={8}>
          <EuiSuperDatePicker
            onTimeChange={onTimeChange}
            start={start}
            onRefresh={onRefresh}
            end={end}
          />
        </Grid>
      </Grid>

      <>
        {selectedOption === "" ? (
          <Box
            sx={{
              height: 400,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EuiEmptyPrompt
              // iconType="logoSecurity"
              title={<h2>Select a website</h2>}
              body={
                <p>
                  Select or add a new website or change your filter settings.
                </p>
              }
              actions={
                <Button
                  component={Link}
                  to="/catalog/add-site"
                  variant="contained"
                  startIcon={<Add />}
                >
                  Add a website
                </Button>
              }
              footer={
                <>
                  <EuiTitle size="xxs">
                    <h3>Want to learn more?</h3>
                  </EuiTitle>
                  <EuiLink
                    href="https://www.hackgate.io/how-does-hackgate-work"
                    target="_blank"
                  >
                    Read the docs
                  </EuiLink>
                </>
              }
            />
          </Box>
        ) : (
          ""
        )}

        {selectedOption !== "" ? (
          <>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Grid item xs></Grid>
              <Grid item>
                <Button variant="outlined" disabled>
                  Generate Report (PDF)
                </Button>
              </Grid>
            </Grid>
              <EuiSpacer/>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <HelpOutlineOutlinedIcon />
                <Typography component="span">
                  CONTROL ID: 01-QUALITY-FRAMEWORK
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Control: Quality Assessment Framework of the Security Testing
                </Typography>
                <Typography>
                  Control Description: Establish and manage a framework for
                  assessing the quality of penetration testing activities,
                  independent of the severity of identified vulnerabilities, in
                  accordance with ISO27001 A.12.6.1, SOC2 CC6.1, and NIST 800-53
                  Rev5 CA-8.
                </Typography>
              </AccordionDetails>
              <AccordionActions>
                <Button variant="contained" startIcon={<PlayArrowIcon />}>
                  Run check
                </Button>
              </AccordionActions>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                {" "}
                <HelpOutlineOutlinedIcon />
                <Typography component="span">
                  CONTROL ID: 02-TESTING-TRAFFIC-MONITORING
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Control: Minimum Security Testing Traffic
                </Typography>
                <Typography>
                  Control Description: Ensure continuous monitoring of the
                  security testing traffic generated during the penetration
                  test, encompassing both manual and automated testing
                  activities, in compliance with ISO27001 A.12.6.1, SOC2 CC6.1,
                  and NIST 800-53 Rev5 CA-8.
                </Typography>
              </AccordionDetails>
              <AccordionActions>
                <Button variant="contained" startIcon={<PlayArrowIcon />}>
                  Run check
                </Button>
              </AccordionActions>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel3-content"
                id="panel3-header"
              >
                <HelpOutlineOutlinedIcon />
                <Typography component="span">
                  CONTROL ID: 03-SECURITY-TESTING-TECHNIQUES
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Control: Adherence to Testing Techniques
                </Typography>
                <Typography>
                  Control Description: Ensure and evaluate the variety of
                  testing techniques employed, benchmarked against the OWASP
                  guidelines, in line with ISO27001 A.12.6.1, SOC2 CC6.1, and
                  NIST 800-53 Rev5 CA-8.
                </Typography>
              </AccordionDetails>
              <AccordionActions>
                <Button variant="contained" startIcon={<PlayArrowIcon />}>
                  Run check
                </Button>
              </AccordionActions>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <HelpOutlineOutlinedIcon />
                <Typography component="span">
                  CONTROL ID: 04-TESTING-DURATION
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>Control: Security Testing Duration</Typography>
                <Typography>
                  Control Description: Monitor the duration of the test and the
                  assets tested in accordance with ISO27001 A.12.6.1, SOC2
                  CC6.1, and NIST 800-53 Rev5 CA-8.
                </Typography>
              </AccordionDetails>
              <AccordionActions>
                <Button variant="contained" startIcon={<PlayArrowIcon />}>
                  Run check
                </Button>
              </AccordionActions>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <HelpOutlineOutlinedIcon />
                <Typography component="span">
                  CONTROL ID: 05-SCOPE-COVERAGE
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>Control: Scope Coverage Monitoring</Typography>
                <Typography>
                  Control Description: Ensure comprehensive coverage of the
                  scope in accordance with ISO27001 A.12.6.1, SOC2 CC6.1, and
                  NIST 800-53 Rev5 CA-8.
                </Typography>
              </AccordionDetails>
              <AccordionActions>
                <Button variant="contained" startIcon={<PlayArrowIcon />}>
                  Run check
                </Button>
              </AccordionActions>
            </Accordion>
            {selectedOption !== "" && <></>}
          </>
        ) : (
          ""
        )}
      </>
    </Layout>
  );
}
